import createMuiTheme, { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';

interface Theme extends ThemeOptions {
  constants: {
    modals: {
      footerHeight: number;
      stepperHeight: number;
    };
  };
}

export const baseTheme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 850,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  palette: {
    primary: {
      main: '#DB1F64',
      light: '#E24C83',
      dark: '#AF1950',
      contrastText: '#FFF'
    },
    secondary: {
      main: '#006DB8',
      light: '#338AC6',
      dark: '#005793',
      contrastText: '#FFF'
    },
    error: {
      main: '#B22F10',
      contrastText: '#FFF'
    },
    warning: {
      main: '#D87125',
      contrastText: '#FFF'
    },
    success: {
      main: '#208C41',
      light: '#4DA367',
      dark: '#16622D',
      contrastText: '#FFF'
    },
    grey: {
      50: '#F5F5F5',
      100: '#EAEAEA',
      200: '#DBDBDB',
      300: '#CACACA',
      400: '#C1C1C1',
      500: '#969696',
      600: '#797979',
      700: '#696969',
      800: '#2D2D2D',
      900: '#1F1F1F',
      A100: '#F0F0F0',
      A200: '#E5E5E5',
      A400: '#B1B1B1',
      A700: '#434343'
    }
  }
});

const theme: Theme = {
  ...baseTheme,
  constants: {
    modals: {
      footerHeight: 80,
      stepperHeight: 24
    }
  },
  typography: {
    fontFamily: 'futura-pt, Arial',
    h1: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 700,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(32),
      lineHeight: baseTheme.typography.pxToRem(48)
    },
    h2: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(28),
      lineHeight: baseTheme.typography.pxToRem(42)
    },
    h3: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(24),
      lineHeight: baseTheme.typography.pxToRem(36)
    },
    h4: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(22),
      lineHeight: baseTheme.typography.pxToRem(33)
    },
    h5: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(20),
      lineHeight: baseTheme.typography.pxToRem(30)
    },
    h6: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(27)
    },
    // P-small and table-cell
    body1: {
      fontFamily: 'futura-pt, sans-serif',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(16),
      lineHeight: baseTheme.typography.pxToRem(24)
    },
    // Not used for now
    body2: {
      fontFamily: 'futura-pt, sans-serif',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: baseTheme.typography.pxToRem(16),
      lineHeight: baseTheme.typography.pxToRem(24)
    },
    // P
    subtitle1: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(18),
      lineHeight: baseTheme.typography.pxToRem(27)
    },
    subtitle2: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 400,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(14),
      lineHeight: baseTheme.typography.pxToRem(21)
    },
    //Helper
    caption: {
      fontFamily: 'futura-pt, sans-serif',
      fontWeight: 500,
      fontStyle: 'normal',
      fontSize: baseTheme.typography.pxToRem(14),
      lineHeight: baseTheme.typography.pxToRem(21)
    }
  },
  overrides: {
    MuiOutlinedInput: {
      root: {
        backgroundColor: baseTheme.palette.common.white,
        opacity: 1,
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
          color: baseTheme.palette.grey[700],
          border: `1px solid ${baseTheme.palette.grey[300]}`
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${baseTheme.palette.grey[800]}`
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          border: `1px solid ${baseTheme.palette.primary.main}`
        },
        '&.MuiOutlinedInput-adornedStart': {
          paddingLeft: baseTheme.typography.pxToRem(12),
          '&& svg': {
            fontSize: baseTheme.typography.pxToRem(16),
            lineHeight: baseTheme.typography.pxToRem(24),
            color: baseTheme.palette.grey[700]
          }
        }
      },
      inputMarginDense: {
        paddingTop: 0,
        paddingBottom: 0
      },
      marginDense: {
        padding: 0
      },
      input: {
        padding: `${baseTheme.spacing(0.625)}px ${baseTheme.spacing(1.5)}px`
      },
      notchedOutline: {
        top: 0,
        border: '1px solid #979797',
        '&.Mui-disabled': {
          border: `1px solid ${baseTheme.palette.grey[300]}`
        },
        '& legend': {
          display: 'none',
          height: 0
        }
      }
    },
    MuiFormLabel: {
      root: {
        '&.Mui-focused.MuiInputLabel-outlined': {
          color: baseTheme.palette.common.black
        }
      }
    },
    MuiInputLabel: {
      outlined: {
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: baseTheme.typography.pxToRem(24),
        fontFamily: 'futura-pt, sans-serif',
        fontStyle: 'normal',
        fontWeight: 600,
        color: baseTheme.palette.common.black,
        transform: 'none',
        '&.MuiInputLabel-marginDense': {
          transform: 'none'
        },
        '&.MuiInputLabel-shrink': {
          transform: 'none'
        }
      },
      formControl: {
        '&.MuiInputLabel-outlined': {
          position: 'unset',
          transform: 'none'
        }
      },
      animated: {
        '&.MuiInputLabel-outlined': {
          transition: 'none'
        }
      },
      root: {
        '&.MuiInputLabel-outlined': {
          '&$error': {
            color: baseTheme.palette.common.black
          },
          '&::active': {
            color: baseTheme.palette.common.black
          },
          '&::focus': {
            color: baseTheme.palette.common.black
          },
          '&.Mui-disabled': {
            color: baseTheme.palette.common.black,
            opacity: 1
          }
        }
      }
    },
    MuiSelect: {
      selectMenu: {
        '&.MuiSelect-outlined': {
          height: baseTheme.typography.pxToRem(33),
          lineHeight: baseTheme.typography.pxToRem(33)
        }
      },
      select: {
        '&.MuiSelect-outlined': {
          '&:focus': {
            backgroundColor: baseTheme.palette.common.white
          },
          '&:active': {
            backgroundColor: baseTheme.palette.common.white
          }
        }
      }
    },
    MuiInputBase: {
      root: {
        '&.MuiOutlinedInput-root': {
          fontSize: baseTheme.typography.pxToRem(16),
          lineHeight: baseTheme.typography.pxToRem(24),
          '&.Mui-disabled': {
            background: '#EAEAEA 0% 0% no-repeat padding-box',
            color: baseTheme.palette.grey[700]
          }
        }
      },
      input: {
        '&.MuiOutlinedInput-input&:not(.MuiSelect-select)': {
          height: baseTheme.typography.pxToRem(33),
          color: baseTheme.palette.grey[800],
          '&::placeholder': {
            color: baseTheme.palette.grey[700],
            fontFamily: 'futura-pt, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            opacity: '1 !important'
          },
          '&.Mui-disabled': {
            color: baseTheme.palette.grey[700]
          }
        },
        'label[data-shrink=false] + $formControl &': {
          '&::-webkit-input-placeholder': { opacity: 1 },
          '&::-moz-placeholder': { opacity: 1 }, // Firefox 19+
          '&:-ms-input-placeholder': { opacity: 1 }, // IE 11
          '&::-ms-input-placeholder': { opacity: 1 }, // Edge
          '&:focus::-webkit-input-placeholder': { opacity: 1 },
          '&:focus::-moz-placeholder': { opacity: 1 }, // Firefox 19+
          '&:focus:-ms-input-placeholder': { opacity: 1 }, // IE 11
          '&:focus::-ms-input-placeholder': { opacity: 1 } // Edge
        }
      }
    },
    MuiButton: {
      root: {
        border: 0,
        borderRadius: 24,
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: baseTheme.typography.pxToRem(24),
        padding: `${baseTheme.spacing(1.125)}px ${baseTheme.spacing(3.5)}px`,
        height: baseTheme.typography.pxToRem(43),
        textTransform: 'initial'
      },
      containedPrimary: {
        background: baseTheme.palette.primary.main,
        color: baseTheme.palette.common.white,
        '&:active': {
          background: baseTheme.palette.primary.dark
        },
        '&:hover': {
          background: baseTheme.palette.primary.light
        }
      },
      containedSecondary: {
        background: baseTheme.palette.secondary.main,
        color: baseTheme.palette.common.white,
        '&:active': {
          background: baseTheme.palette.secondary.dark
        },
        '&:hover': {
          background: baseTheme.palette.secondary.light
        }
      },
      iconSizeSmall: {
        '& > *:first-child': {
          fontSize: baseTheme.typography.pxToRem(16)
        }
      },
      iconSizeMedium: {
        '& > *:first-child': {
          fontSize: baseTheme.typography.pxToRem(16)
        }
      },
      iconSizeLarge: {
        '& > *:first-child': {
          fontSize: baseTheme.typography.pxToRem(18)
        }
      },
      outlined: {
        background: baseTheme.palette.common.white,
        borderRadius: 24,
        padding: `${baseTheme.spacing(1.125)}px ${baseTheme.spacing(3.5)}px`,
        height: baseTheme.typography.pxToRem(43),
        '&$disabled': {
          background: baseTheme.palette.common.white,
          border: `1px solid ${baseTheme.palette.grey[500]}`,
          color: baseTheme.palette.grey[500]
        }
      },
      outlinedPrimary: {
        border: `2px solid ${baseTheme.palette.primary.main}`,
        color: baseTheme.palette.primary.main,
        '&:active': {
          background: baseTheme.palette.grey[200],
          borderColor: baseTheme.palette.primary.dark,
          borderWidth: '2px'
        },
        '&:hover': {
          background: baseTheme.palette.grey[100],
          borderColor: baseTheme.palette.primary.light,
          borderWidth: '2px'
        },
        '&$disabled': {
          borderWidth: '2px'
        }
      },
      outlinedSecondary: {
        border: `1px solid ${baseTheme.palette.grey[700]}`,
        color: baseTheme.palette.common.black,
        backgroundColor: baseTheme.palette.common.white,
        '&:active': {
          background: baseTheme.palette.grey[300]
        },
        '&:hover': {
          backgroundColor: baseTheme.palette.grey[100],
          border: `1px solid ${baseTheme.palette.grey[700]}`
        }
      },
      sizeSmall: {
        padding: `${baseTheme.spacing(0.5)}px ${baseTheme.spacing(2.5)}px`,
        height: baseTheme.typography.pxToRem(33),
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: baseTheme.typography.pxToRem(21)
      },
      sizeLarge: {
        padding: `${baseTheme.spacing(1.125)}px ${baseTheme.spacing(3.5)}px`,
        height: baseTheme.typography.pxToRem(43),
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: baseTheme.typography.pxToRem(21)
      },
      startIcon: {
        marginRight: baseTheme.spacing(0.5)
      },
      text: {
        color: baseTheme.palette.secondary.main,
        padding: `${baseTheme.spacing(1.375)}px ${baseTheme.spacing(3.5)}px`,
        textTransform: 'initial',
        '&$disabled': {
          color: baseTheme.palette.grey[500]
        }
      },
      textPrimary: {
        color: baseTheme.palette.secondary.main,
        '&:active': {
          color: baseTheme.palette.secondary.dark
        },
        '&:hover': {
          color: baseTheme.palette.secondary.dark
        }
      },
      textSecondary: {
        color: baseTheme.palette.primary.main,
        '&:active': {
          color: baseTheme.palette.primary.dark
        },
        '&:hover': {
          color: baseTheme.palette.primary.dark
        }
      },
      textSizeSmall: {
        lineHeight: baseTheme.typography.pxToRem(16)
      }
    },
    MuiFormControlLabel: {
      label: {
        marginLeft: baseTheme.spacing(2)
      }
    },
    MuiFormHelperText: {
      root: {
        marginTop: 0,
        fontSize: baseTheme.typography.pxToRem(14),
        lineHeight: baseTheme.typography.pxToRem(21),
        fontWeight: 400,
        '&.Mui-error': {
          color: baseTheme.palette.primary.main
        }
      },
      marginDense: {
        marginTop: 0
      },
      contained: {
        color: baseTheme.palette.primary.main,
        marginLeft: 0,
        marginRight: 0
      }
    },
    MuiSwitch: {
      root: {
        width: 48,
        height: 26,
        padding: 0
      },
      colorPrimary: {
        '&$disabled$checked': {
          color: '#ffffff'
        },
        '&$checked': {
          color: 'white',
          border: 'none',
          opacity: 1
        },
        '&$checked + $track': {
          backgroundColor: '#208c41',
          border: 'none',
          opacity: 1
        },
        '&$disabled$checked + $track': {
          backgroundColor: '#8fc5a0'
        },
        '&$disabled + $thumb': {
          color: 'red'
        }
      },
      colorSecondary: {
        '&$disabled': {
          color: '#cacaca'
        },
        '&$disabled$checked': {
          color: '#ffffff'
        },
        '&$checked': {
          color: 'white',
          border: 'none',
          opacity: 1
        },
        '&$disabled + $track': {
          backgroundColor: '#eaeaea',
          borderColor: '#cacaca'
        },
        '&$checked + $track': {
          backgroundColor: '#208c41',
          border: 'none',
          opacity: 1
        },
        '&$disabled$checked + $track': {
          backgroundColor: '#8fc5a0'
        }
      },
      switchBase: {
        padding: 1,
        top: 1,
        left: 3,
        color: '#696969',
        '&$checked': {
          top: 0,
          transform: 'translateX(21px)'
        },
        '&$disabled + $track': {
          opacity: 'unset'
        }
      },
      thumb: {
        marginTop: 2,
        width: 18,
        height: 18,
        boxShadow: 'unset'
      },
      track: {
        height: 24,
        width: 48,
        borderRadius: 24 / 2,
        border: `1px solid #696969`,
        backgroundColor: '#eaeaea',
        opacity: 1,
        transition:
          'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&$disabled + $track': {
          backgroundColor: '#eaeaea',
          borderColor: '#cacaca'
        }
      },
      checked: {}
    },
    MuiTabs: {
      indicator: {
        display: 'none'
      }
    },
    MuiTab: {
      root: {
        borderRadius: '5px',
        textTransform: 'none',
        margin: baseTheme.spacing(1)
      },
      textColorSecondary: {
        fontWeight: baseTheme.typography.fontWeightRegular,
        fontSize: baseTheme.typography.pxToRem(16),
        lineHeight: baseTheme.typography.pxToRem(24),
        color: baseTheme.palette.grey[800],
        '&.Mui-selected': {
          backgroundColor: baseTheme.palette.secondary.main,
          color: baseTheme.palette.common.white
        }
      }
    }
  }
};

export default createMuiTheme(theme);
